import { Button, HStack, Stack } from '@chakra-ui/react'
import type { AuctionWithHighestBid } from '@twlvxtwlv/supabase'
import type { NotifyMeAttributes, RoadmapEntry } from '@twlvxtwlv/types'
import { toLocalizedDate } from '@twlvxtwlv/utils/localization'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { RoadmapListItem } from './roadmap-list-item'
import { RoadmapListItemContent } from './roadmap-list-item-content'

type RoadmapListProps = {
  auctionsWithHighestBid: AuctionWithHighestBid[]
  items: RoadmapEntry[]
  onOpenNotifyMeModal: () => void
  onNotifyMeAttributesChanged: (attributes: NotifyMeAttributes | null) => void
}

export function RoadmapList({
  auctionsWithHighestBid,
  items,
  onOpenNotifyMeModal,
  onNotifyMeAttributesChanged,
}: RoadmapListProps) {
  const { t } = useTranslation('nft-details')
  const router = useRouter()

  const [showAll, setShowAll] = useState<boolean>(false)

  sortRoadmapItemsAscending(items)
  overwriteBulletPointColors(items)

  const yesterday = new Date(Date.now() - 86400000).toISOString() //  24 * 60 * 60 * 1000 (1 day)

  return (
    <HStack bg="black" overflowY="auto">
      <Stack spacing={0}>
        <RoadmapListItem isFirstEntry={true}>
          {!showAll ? (
            <Button
              variant="txtBlack"
              borderColor="white"
              bg="transparent"
              onClick={() => setShowAll(true)}
            >
              {t('road-map-previous-nfts')}
            </Button>
          ) : null}
        </RoadmapListItem>
        {items?.map((item, index) => {
          const displayDate = item.startAtText
            ? item.startAtText
            : toLocalizedDate(item.startAt, router.locale ?? 'de')
          return item.currentlyActive ||
            (item.startAt && new Date(item.startAt).toISOString() >= yesterday) ||
            showAll ||
            //Show at least 5 items
            index > items.length - 6 ? (
            <RoadmapListItem
              key={index}
              bulletPointColor={item.overwriteBulletPointColor ?? 'white'}
            >
              <RoadmapListItemContent
                auctionsWithHighestBid={auctionsWithHighestBid}
                roadmapItem={item}
                displayDate={displayDate}
                availability={item.availability}
                onOpenNotifyMeModal={onOpenNotifyMeModal}
                onNotifyMeAttributesChanged={onNotifyMeAttributesChanged}
              />
            </RoadmapListItem>
          ) : null
        })}
        <RoadmapListItem isLastEntry={true} />
      </Stack>
    </HStack>
  )
}

function overwriteBulletPointColors(items: RoadmapEntry[]) {
  if (items.filter((item) => item.currentlyActive).length > 0) {
    let beforeCurrentlyActive = true
    for (let index = 0; index < items.length; index++) {
      items[index].overwriteBulletPointColor = beforeCurrentlyActive
        ? 'txtDeutschRapPurple'
        : 'txtDeutschRapPurple'

      if (!beforeCurrentlyActive) break
      if (items[index].currentlyActive) beforeCurrentlyActive = false
    }
  }
}

// Sort from past to future
function sortRoadmapItemsAscending(items: RoadmapEntry[]) {
  items
    .filter((item) => item && item.startAt !== null)
    .sort(
      (item1, item2) =>
        Date.parse(item1.startAt!.toString()) - Date.parse(item2.startAt!.toString())
    )
}
